import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type {
  UseCartStateOptions,
  UseCartStateResult,
  ICartItemData,
} from "@model/Cart.model";
import { UserCartStoreImplementation } from "Data/Cart/CartStoreImplementation";
import { CartService } from "../../CartService";

export function useCartState(
  opts: UseCartStateOptions = {}
): UseCartStateResult & { deselect: (id: string) => void } {
  const { isLoggedIn = false } = opts;
  const cartService = new CartService();

  const {
    itemsICart,
    subtotal: storeSubtotal,
    currency,
    removeItemFromCart,
    setCartItemQuantity,
  } = UserCartStoreImplementation(
    "USD",
    cartService.mapCartStateToICartItems,
    cartService.computeCartSubtotalFromICartItems
  );

  const items = itemsICart;

  const setItems = (() =>
    (() => {}) as Dispatch<SetStateAction<ICartItemData[]>>)();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const allIds = useMemo(() => items.map((i) => i.id), [items]);

  const allSelected = useMemo(
    () => items.length > 0 && items.every((i) => selectedIds.has(i.id)),
    [items, selectedIds]
  );
  const noneSelected = useMemo(() => selectedIds.size === 0, [selectedIds]);

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const setAll = useCallback(
    (checked: boolean) => setSelectedIds(checked ? new Set(allIds) : new Set()),
    [allIds]
  );

  const deleteOne = useCallback(
    (id: string) => {
      removeItemFromCart(id);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [removeItemFromCart]
  );

  const changeQty = useCallback(
    (id: string, qty: number) => {
      setCartItemQuantity(id, Math.max(0, Math.floor(qty || 0)));
    },
    [setCartItemQuantity]
  );

  const deselect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const selectedItems = useMemo<ICartItemData[]>(
    () => items.filter((i) => selectedIds.has(i.id)),
    [items, selectedIds]
  );

  const selectedSubtotal = useMemo(
    () => cartService.computeCartSubtotalFromICartItems(selectedItems),
    [selectedItems]
  );

  const subtotal = storeSubtotal;

  const disableCheckout = useMemo(
    () => !isLoggedIn || items.length === 0,
    [isLoggedIn, items.length]
  );

  const didAutoSelectRef = useRef<true | null>(null);
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (items.length > 0 && selectedIds.size === 0) {
      setSelectedIds(new Set(allIds));
      didAutoSelectRef.current = true;
    }
  }, [items.length, selectedIds.size, allIds]);

  return {
    items,
    setItems,
    selectedIds,
    allSelected,
    noneSelected,
    toggleOne,
    setAll,
    deleteOne,
    changeQty,
    subtotal,
    currency,
    disableCheckout,
    selectedItems,
    selectedSubtotal,
    safeCurrency: currency,
    deselect,
  };
}
